/**
 * Inline-error bookkeeping for Settings → Notifications (Stage 5 QA carry-over):
 * Save validates everything at once; an edit to a field then clears only that
 * field's message, so the page never nags about something already fixed.
 */
import type { ContactHours, NudgeErrors, RecipientField, SettingsErrors } from './types';

export type ErrorTouch =
  | { section: 'contact_hours'; field: keyof ContactHours }
  | { section: 'nudge'; field: keyof NudgeErrors }
  | { section: 'recipient'; id: string; field: RecipientField };

/** Changing the end time can also fix "End must be after start", so `start`/`end` clear each other. */
const HOURS_PAIR: Record<keyof ContactHours, ReadonlyArray<keyof ContactHours>> = {
  tz: ['tz'],
  start: ['start', 'end'],
  end: ['start', 'end'],
};

/** Switching channel flips which contact field is required, so both clear. */
const RECIPIENT_PAIR: Partial<Record<RecipientField, ReadonlyArray<'name' | 'phone' | 'slack_channel'>>> = {
  name: ['name'],
  phone: ['phone'],
  slack_channel: ['slack_channel'],
  channel: ['phone', 'slack_channel'],
};

export function clearError(errors: SettingsErrors, touch: ErrorTouch): SettingsErrors {
  if (touch.section === 'contact_hours') {
    if (!errors.contact_hours) return errors;
    const next = { ...errors.contact_hours };
    for (const k of HOURS_PAIR[touch.field]) delete next[k];
    return { ...errors, contact_hours: Object.keys(next).length ? next : undefined };
  }
  if (touch.section === 'nudge') {
    if (!errors.nudge?.[touch.field]) return errors;
    const next = { ...errors.nudge };
    delete next[touch.field];
    return { ...errors, nudge: Object.keys(next).length ? next : undefined };
  }
  const fields = RECIPIENT_PAIR[touch.field];
  const current = errors.recipients[touch.id];
  if (!fields || !current) return errors;
  const next = { ...current };
  for (const k of fields) delete next[k];
  const recipients = { ...errors.recipients };
  if (Object.keys(next).length) recipients[touch.id] = next;
  else delete recipients[touch.id];
  return { ...errors, recipients };
}
